import { type ReactNode } from 'react';
import { Button } from '@carbon/react';
import { AddComment } from '@carbon/react/icons';
import { type WorkingConfigForm } from './hooks';
import { ActionsMenu } from './actions-menu';
import { EvalSettingsToggle } from './eval-settings-toggle';
import {
  useSaveEvaluationController,
  SaveEvaluationButton,
} from './save-evaluation';

interface HeaderActionsProps {
  /** The lifted working-config form (owned by `Eval3NewContent`) — for the Save dirty gate. */
  workingConfig: WorkingConfigForm;
  /**
   * Opens the shared activity drawer in package mode. Package activity normally lives in the
   * left pane beside the package title, so the parent passes this ONLY while that pane is
   * collapsed — a collapsed pane is `inert`, which would otherwise strip the pane's button from
   * the tab order and the a11y tree and leave package activity unreachable. Exactly one package
   * affordance exists at any moment.
   */
  onOpenPackageActivity?: () => void;
  /**
   * Opens the shared activity drawer in calculation mode. The parent omits this when no
   * calc is selected (browse/draft routes), which is what hides the button.
   */
  onOpenCalcActivity?: () => void;
  /**
   * The Insights agent's trigger, when this page can host a conversation. Passed in rather
   * than rendered here so `HeaderActions` stays free of agent wiring; the parent omits it
   * when the workspace exposes no agent endpoint. The parent also sizes it — see the note
   * on heights below.
   */
  agentTrigger?: ReactNode;
}

/**
 * The center title bar's scoped actions (mock §1). The Actions menu (Duplicate / Export / …),
 * the Eval Settings toggle, and Save all render inline and STAY visible at every width — they no
 * longer fold into the Actions menu when the window shrinks. Overlap with the Eval Settings panel
 * on a narrow window is prevented in the header layout instead (center-header.module.scss: the
 * title truncates and this action cluster is pinned, `flex-shrink: 0`), so the buttons keep their
 * place rather than disappearing into a dropdown. The Save state + toast live in a controller
 * owned HERE (a persistently-mounted spot) so the save-success/error toast survives Save.
 *
 * The activity buttons are icon-only, tertiary + md to match the height of the Actions /
 * Eval Settings / Save cluster they sit beside; the left pane's Package Activity button is sm to
 * suit the narrower pane. The agent trigger sits leftmost and stays inline too — it is the
 * page's primary way in to the assistant, so it should not disappear into a menu. It is passed
 * at md for the same reason the activity buttons are: everything in this cluster is one height.
 * `AgentTriggerButton` defaults to sm, which is what its other hosts sit beside.
 *
 * @param {HeaderActionsProps} props - The working-config form and the activity-drawer openers.
 * @return {JSX.Element} The inline Actions cluster plus the Save toast.
 */
export function HeaderActions({
  workingConfig,
  onOpenPackageActivity,
  onOpenCalcActivity,
  agentTrigger,
}: HeaderActionsProps): JSX.Element {
  const save = useSaveEvaluationController(workingConfig);

  return (
    <>
      {onOpenPackageActivity && (
        <Button
          hasIconOnly
          kind="tertiary"
          size="md"
          renderIcon={AddComment}
          iconDescription="Package Activity"
          tooltipAlignment="center"
          tooltipPosition="bottom"
          onClick={onOpenPackageActivity}
        />
      )}
      {onOpenCalcActivity && (
        <Button
          hasIconOnly
          kind="tertiary"
          size="md"
          renderIcon={AddComment}
          iconDescription="Evaluation Activity"
          tooltipAlignment="center"
          tooltipPosition="bottom"
          onClick={onOpenCalcActivity}
        />
      )}
      {agentTrigger}
      <ActionsMenu workingConfig={workingConfig} />
      <EvalSettingsToggle />
      {save.visible && <SaveEvaluationButton controller={save} />}
      {/* Persistently mounted so the save-success/error toast survives the click. */}
      {save.toast}
    </>
  );
}
